import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPartnersService } from "@/services/admin-partners-service";
import type {
  AdminTenantPartnersSettings,
  TenantPartnerCommission,
  UpdateTenantPartnerCommissionRequest,
} from "@/types/partner-settings";

// Tasks 0028/0029 — Guesteria-staff-only tenant controls (SUPER_ADMIN /
// GUESTERIA_ADMIN), rendered in `TenantPartnersSection` on the tenant-info
// tab of `TenantEditPage.tsx`. Contract 2026-09-28.

export const tenantPartnersAdminKeys = {
  all: ["tenantPartnersAdmin"] as const,
  enabled: (tenantId: string) =>
    [...tenantPartnersAdminKeys.all, "enabled", tenantId] as const,
  commission: (tenantId: string) =>
    [...tenantPartnersAdminKeys.all, "commission", tenantId] as const,
};

export const useTenantPartnersEnabled = (
  tenantId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: tenantId
      ? tenantPartnersAdminKeys.enabled(tenantId)
      : tenantPartnersAdminKeys.all,
    queryFn: () =>
      adminPartnersService.getTenantPartnersEnabled(tenantId ?? ""),
    staleTime: 60 * 1000,
    enabled: enabled && tenantId !== null,
  });

export const useUpdateTenantPartnersEnabled = (tenantId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AdminTenantPartnersSettings) =>
      adminPartnersService.updateTenantPartnersEnabled(tenantId ?? "", data),
    onSuccess: () => {
      if (!tenantId) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: tenantPartnersAdminKeys.enabled(tenantId),
      });
    },
  });
};

export const useTenantPartnerCommission = (
  tenantId: string | null,
  enabled: boolean,
) =>
  useQuery<TenantPartnerCommission>({
    queryKey: tenantId
      ? tenantPartnersAdminKeys.commission(tenantId)
      : tenantPartnersAdminKeys.all,
    queryFn: () =>
      adminPartnersService.getTenantPartnerCommission(tenantId ?? ""),
    staleTime: 60 * 1000,
    enabled: enabled && tenantId !== null,
  });

export const useUpdateTenantPartnerCommission = (tenantId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTenantPartnerCommissionRequest) =>
      adminPartnersService.updateTenantPartnerCommission(tenantId ?? "", data),
    onSuccess: () => {
      if (!tenantId) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: tenantPartnersAdminKeys.commission(tenantId),
      });
    },
  });
};
